import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createEditor, Editor, Element as SlateElement, Range, Transforms } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { htmlToSlate, slateToHtml } from '@slate-serializers/html';
import Button from './Button';

type Props = {
  value: string; // HTML in
  onChange: (html: string) => void; // HTML out
};

const EMPTY_VALUE: any[] = [{ type: 'paragraph', children: [{ text: '' }] }];

const LIST_TYPE = 'ul';
const LIST_ITEM_TYPE = 'li';
const LINK_TYPE = 'link';

function toggleMark(editor: Editor, mark: 'bold' | 'italic' | 'underline') {
  const marks = Editor.marks(editor) as any;
  const isActive = marks ? marks[mark] === true : false;
  if (isActive) Editor.removeMark(editor, mark);
  else Editor.addMark(editor, mark, true);
}

function isMarkActive(editor: Editor, mark: 'bold' | 'italic' | 'underline') {
  const marks = Editor.marks(editor) as any;
  return marks ? marks[mark] === true : false;
}

function isBlockActive(editor: Editor, type: string) {
  const iter = Editor.nodes(editor, {
    match: (n: any) => !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any).type === type,
  });
  return !!iter.next().value;
}

/** -------- Lists -------- */

function unwrapList(editor: Editor) {
  Transforms.unwrapNodes(editor, {
    match: (n: any) => !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any).type === LIST_TYPE,
    split: true,
  });
}

function toggleBulletedList(editor: Editor) {
  const isActive = isBlockActive(editor, LIST_TYPE);

  // Remove list wrapper first (so toggling is predictable)
  unwrapList(editor);

  // Switch selected blocks between <li> and <p>
  Transforms.setNodes(
    editor,
    { type: isActive ? 'paragraph' : LIST_ITEM_TYPE } as any,
    { match: (n: any) => SlateElement.isElement(n) && Editor.isBlock(editor, n) }
  );

  // If turning on, wrap the list-items with <ul>
  if (!isActive) {
    Transforms.wrapNodes(
      editor,
      { type: LIST_TYPE, children: [] } as any,
      { match: (n: any) => SlateElement.isElement(n) && (n as any).type === LIST_ITEM_TYPE }
    );
  }
}

/** -------- Links -------- */

function isLinkActive(editor: Editor) {
  const iter = Editor.nodes(editor, {
    match: (n: any) => !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any).type === LINK_TYPE,
  });
  return !!iter.next().value;
}

function unwrapLink(editor: Editor) {
  Transforms.unwrapNodes(editor, {
    match: (n: any) => !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any).type === LINK_TYPE,
    split: true,
  });
}

function wrapLink(editor: Editor, url: string) {
  if (!url) return;

  // If selection is inside an existing link, replace it
  if (isLinkActive(editor)) unwrapLink(editor);

  const { selection } = editor;
  const isCollapsed = !!selection && Range.isCollapsed(selection);

  const linkNode: any = {
    type: LINK_TYPE,
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    // Insert a link with the URL as text
    Transforms.insertNodes(editor, linkNode);
  } else {
    // Wrap the selected text
    Transforms.wrapNodes(editor, linkNode, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
}

function toggleLink(editor: Editor) {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
    return;
  }

  const url = window.prompt('Enter URL');
  if (!url) return;

  wrapLink(editor, url);
}

export default function HtmlEditor({ value, onChange }: Props) {
  const editor = useMemo(() => {
    const e = withHistory(withReact(createEditor() as ReactEditor));
    const { isInline } = e;

    // Make <a> behave like inline text
    e.isInline = (element: any) => ((element as any).type === LINK_TYPE ? true : isInline(element));

    return e;
  }, []);

  const [slateValue, setSlateValue] = useState<any[]>(EMPTY_VALUE);
  const [showEditor, setShowEditor] = useState('wysiwyg')

  const lastAppliedHtml = useRef<string>('');
  const lastEmittedHtml = useRef<string>('');

  // HTML prop -> replace editor content
  useEffect(() => {
    const incoming = value ?? '';
    if (incoming === lastAppliedHtml.current) return;
    if (incoming === lastEmittedHtml.current) return;

    const nodes = incoming ? (htmlToSlate(incoming) as any[]) : EMPTY_VALUE;

    // Replace the editor's document (Slate is uncontrolled; initialValue won't update after mount)
    editor.children = nodes as any;
    editor.onChange();

    setSlateValue(nodes);
    lastAppliedHtml.current = incoming;
  }, [value, editor]);

  const renderLeaf = useCallback((props: any) => {
    const { attributes, children, leaf } = props;
    let out = children;
    if (leaf.bold) out = <strong>{out}</strong>;
    if (leaf.italic) out = <em>{out}</em>;
    if (leaf.underline) out = <u>{out}</u>;
    return <span {...attributes}>{out}</span>;
  }, []);

  const renderElement = useCallback((props: any) => {
    const { attributes, children, element } = props;

    switch (element.type) {
      case 'link':
        return (
          <a
            {...attributes}
            href={(element as any).url}
            style={{
              color: '#0b5fff',
              textDecoration: 'underline',
            }}
            onClick={(e) => e.preventDefault()} // don't navigate while editing
          >
            {children}
          </a>
        );

      case 'ul':
        return (
          <ul
            {...attributes}
            style={{
              margin: '0 0 0.75em 0',
              paddingLeft: '1.25em',
              listStyleType: 'disc',
              listStylePosition: 'outside',
            }}
          >
            {children}
          </ul>
        );

      case 'li':
        return (
          <li
            {...attributes}
            style={{
              display: 'list-item',
              listStyleType: 'disc',
            }}
          >
            {children}
          </li>
        );

      default:
        // paragraph
        return (
          <p
            {...attributes}
            style={{
              margin: '0 0 0.75em 0', // blank line under paragraphs
              whiteSpace: 'pre-wrap',
            }}
          >
            {children}
          </p>
        );
    }
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!editor.selection) return;

      // Find current <li> if any (generator-safe: .next())
      const liIter = Editor.nodes(editor, {
        match: (n: any) =>
          !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any).type === LIST_ITEM_TYPE,
      });
      const liEntry = liIter.next().value as any | undefined;

      // Enter on an empty <li> -> exit list (turn into paragraph)
      if (event.key === 'Enter' && liEntry) {
        const [, liPath] = liEntry;
        const isEmpty = Editor.string(editor, liPath) === '';
        if (isEmpty) {
          event.preventDefault();
          unwrapList(editor);
          Transforms.setNodes(editor, { type: 'paragraph' } as any);
          return;
        }
      }

      // Backspace at start of <li> -> exit list
      if (event.key === 'Backspace' && liEntry && Range.isCollapsed(editor.selection)) {
        const [, liPath] = liEntry;
        const start = Editor.start(editor, liPath);
        if (Editor.isStart(editor, editor.selection.anchor, start)) {
          event.preventDefault();
          unwrapList(editor);
          Transforms.setNodes(editor, { type: 'paragraph' } as any);
          return;
        }
      }

      // Optional: Ctrl/Cmd+K to toggle link
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggleLink(editor);
        return;
      }
    },
    [editor]
  );

  const isListActive = isBlockActive(editor, LIST_TYPE);

  return <div className={`flex flex-col gap-4`}>

    <div className={`mt-2 flex flex-row gap-1`}>
      <Button
        text={'Visual editor'}
        onClick={() => setShowEditor('wysiwyg')}
        fullwidth={false}
        variant={showEditor === 'wysiwyg' ? 'primary' : 'gray'}
      />
      <Button
        text={'HTML code editor'}
        onClick={() => setShowEditor('html')}
        fullwidth={false}
        variant={showEditor === 'html' ? 'primary' : 'gray'}
      />
    </div>

    {showEditor === 'wysiwyg' && <div style={{ border: '1px solid #ccc', borderRadius: 6 }}>
      <Slate
        editor={editor}
        initialValue={slateValue}
        onValueChange={(v: any) => {
          setSlateValue(v as any);

          const html = slateToHtml(v as any);
          if (html === lastEmittedHtml.current) return;

          lastEmittedHtml.current = html;
          onChange(html);
        }}
      >

        <div style={{ padding: 8, borderBottom: '1px solid #ccc', display: 'flex', gap: 8 }}>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleMark(editor, 'bold');
            }}
            style={{ fontWeight: isMarkActive(editor, 'bold') ? 'bold' : 'normal' }}
          >
            B
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleMark(editor, 'italic');
            }}
            style={{ fontStyle: isMarkActive(editor, 'italic') ? 'italic' : 'normal' }}
          >
            I
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleMark(editor, 'underline');
            }}
            style={{ textDecoration: isMarkActive(editor, 'underline') ? 'underline' : 'none' }}
          >
            U
          </button>

          <span style={{ width: 1, background: '#ccc', margin: '0 4px' }} />

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleBulletedList(editor);
            }}
            style={{ fontWeight: isListActive ? 'bold' : 'normal' }}
            aria-pressed={isListActive}
            title="Bulleted list"
          >
            • List
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleLink(editor);
            }}
            style={{ textDecoration: isLinkActive(editor) ? 'underline' : 'none' }}
            title="Insert/remove link (Ctrl/Cmd+K)"
          >
            🔗
          </button>
        </div>

        <Editable
          placeholder="Type here..."
          renderLeaf={renderLeaf}
          renderElement={renderElement}
          onKeyDown={onKeyDown}
          style={{ padding: 12, minHeight: 140 }}
        />

      </Slate>
    </div>}


    {showEditor === 'html' && <textarea
      className={`text-sm px-3 py-2 bg-white drop-shadow-lg border border-gray-300 text-gray-900 rounded hover:opacity-90-lg focus:ring-blue-500 focus:border-blue-500 block w-full min-h-[140px]`}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    />}


  </div>

}
